/* Akoviya cart drawer (sections/akoviya-cart-drawer.liquid).
   - Takes over Dawn's "added to cart" pop-up: CartNotification.renderContents is wrapped at runtime (no Dawn file is
     edited) so an add opens this drawer instead. Turn "Open when something is added" off and Dawn's pop-up is back.
   - All cart changes go through Shopify's Ajax cart API with the Section Rendering API, and only the
     [data-acd-dyn] parts are swapped. The checkout area is never touched, so Shiprocket's BUY NOW (which its own script
     builds inside .shiprocket-headless[data-type="mini-cart"]) stays in place. */
(() => {
  if (customElements.get('akoviya-cart-drawer')) return;

  const SEP = '%%SEP%%';
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';

  // Resolves { ok, data }; a non-JSON answer (e.g. Shopify's 429 "too many requests" page) becomes ok:false.
  const post = (path, body) =>
    fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json().then((data) => ({ ok: r.ok, data }), () => ({ ok: false, data: {} })));

  class AkoviyaCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.sectionId = this.dataset.section;
      this.panel = this.querySelector('[data-acd-panel]');

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-acd-input]');
        if (!input) return;
        const line = input.closest('[data-acd-line]');
        const qty = Math.max(0, parseInt(input.value, 10) || 0);
        this.change(line, qty);
      });
      this.addEventListener('keydown', (e) => this.onKey(e));
      const disc = this.querySelector('[data-acd-disc]');
      if (disc) disc.addEventListener('submit', (e) => { e.preventDefault(); this.applyCode(); });

      this.onDocClick = (e) => {
        if (this.dataset.openIcon !== 'true' || e.defaultPrevented || e.button > 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const icon = e.target.closest('#cart-icon-bubble');
        if (!icon) return;
        e.preventDefault();
        this.refresh().finally(() => this.open(icon));
      };
      document.addEventListener('click', this.onDocClick);

      this.onPageShow = (e) => { if (e.persisted) this.refresh(); };
      window.addEventListener('pageshow', this.onPageShow);

      this.watchCheckout();

      if (window.Shopify && window.Shopify.designMode) {
        this.onSelect = (e) => { if (e.detail.sectionId === this.sectionId) this.open(); };
        this.onDeselect = (e) => { if (e.detail.sectionId === this.sectionId) this.close(); };
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.onSelect) {
        document.removeEventListener('shopify:section:select', this.onSelect);
        document.removeEventListener('shopify:section:deselect', this.onDeselect);
      }
      if (this.srObserver) this.srObserver.disconnect();
      clearTimeout(this.fallbackTimer);
    }

    get isOpen() { return this.classList.contains('is-open'); }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.hidden = false;
      void this.offsetWidth; // let the closed position paint so the slide animates
      this.classList.add('is-open');
      document.documentElement.classList.add('acd-lock');
      setTimeout(() => this.panel.focus({ preventScroll: true }), 60);
      document.dispatchEvent(new CustomEvent('akoviya:cart-drawer:open'));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('acd-lock');
      if (this.opener && this.opener.focus && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('akoviya:cart-drawer:close'));
    }

    onClick(e) {
      if (e.target.closest('[data-acd-close]')) return this.close();
      if (e.target.closest('[data-acd-overlay]')) { if (this.dataset.overlayClose === 'true') this.close(); return; }

      const qtyBtn = e.target.closest('[data-acd-qty]');
      if (qtyBtn) return this.change(qtyBtn.closest('[data-acd-line]'), parseInt(qtyBtn.dataset.acdQty, 10) || 0);

      const add = e.target.closest('[data-acd-add]');
      if (add) return this.add(add.dataset.acdAdd);

      const uncode = e.target.closest('[data-acd-uncode]');
      if (uncode) return this.removeCode(uncode.dataset.acdUncode);

      const fallback = e.target.closest('[data-acd-fallback]');
      if (fallback) return this.checkoutFallback(e);
    }

    onKey(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- rendering ---------- */

    sectionsParam() { return [this.sectionId, 'cart-icon-bubble']; }

    refresh() {
      return fetch(`${root()}?sections=${this.sectionsParam().join(',')}`)
        .then((r) => r.json())
        .then((sections) => this.render(sections))
        .catch(() => {});
    }

    render(sections) {
      if (!sections) return;
      const html = sections[this.sectionId];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('akoviya-cart-drawer');
        if (fresh) {
          this.querySelectorAll('[data-acd-dyn]').forEach((part) => {
            const next = fresh.querySelector(`[data-acd-dyn="${part.dataset.acdDyn}"]`);
            if (!next) return;
            part.innerHTML = next.innerHTML;
            [...next.attributes].forEach((a) => { if (a.name.startsWith('data-')) part.setAttribute(a.name, a.value); });
          });
          this.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
        }
      }
      const bubble = sections['cart-icon-bubble'];
      const target = document.getElementById('cart-icon-bubble');
      if (bubble && target) {
        const doc = new DOMParser().parseFromString(bubble, 'text/html');
        const inner = doc.querySelector('.shopify-section') || doc.body;
        target.innerHTML = inner.innerHTML;
      }
    }

    busy(on) { this.classList.toggle('is-busy', on); }

    afterChange(cart) {
      document.dispatchEvent(new CustomEvent('akoviya:cart-updated', { detail: { cart } }));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'akoviya-cart-drawer', cartData: cart });
      }
    }

    /* ---------- cart actions ---------- */

    change(lineEl, quantity) {
      if (!lineEl) return;
      const line = parseInt(lineEl.dataset.acdLine, 10);
      this.busy(true);
      return post('cart/change.js', { line, quantity, sections: this.sectionsParam(), sections_url: location.pathname })
        .then(({ ok, data }) => {
          if (!ok || data.status) {
            const err = lineEl.querySelector('[data-acd-err]');
            return this.refresh().then(() => {
              const fresh = this.querySelector(`[data-acd-line="${line}"] [data-acd-err]`) || err;
              if (fresh) { fresh.textContent = data.description || data.message || this.dataset.errGeneric || ''; fresh.hidden = false; }
            });
          }
          this.render(data.sections);
          this.afterChange(data);
        })
        .catch(() => this.refresh())
        .finally(() => this.busy(false));
    }

    add(variantId) {
      this.busy(true);
      return post('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }], sections: this.sectionsParam(), sections_url: location.pathname })
        .then(({ ok, data }) => {
          if (!ok || data.status) return this.refresh();
          this.render(data.sections);
          this.afterChange(data);
        })
        .catch(() => this.refresh())
        .finally(() => this.busy(false));
    }

    currentCodes() {
      const holder = this.querySelector('[data-acd-dyn="summary"]');
      return ((holder && holder.dataset.codes) || '').split(SEP).map((c) => c.trim()).filter(Boolean);
    }

    showDiscError(msg) {
      const el = this.querySelector('[data-acd-disc-err]');
      if (!el) return;
      el.textContent = msg || '';
      el.hidden = !msg;
    }

    updateCodes(codes) {
      return post('cart/update.js', { discount: codes.join(','), sections: this.sectionsParam(), sections_url: location.pathname });
    }

    applyCode() {
      const input = this.querySelector('[data-acd-disc] input');
      const code = (input.value || '').trim();
      this.showDiscError('');
      if (!code) return this.showDiscError(this.dataset.errEmpty);
      const codes = this.currentCodes();
      if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) return this.showDiscError(this.dataset.errAlready);

      this.busy(true);
      return this.updateCodes([...codes, code])
        .then(({ ok, data }) => {
          if (!ok && !data.discount_codes) return this.showDiscError(this.dataset.errGeneric);
          const found = ok && Array.isArray(data.discount_codes)
            ? data.discount_codes.find((c) => (c.code || '').toLowerCase() === code.toLowerCase())
            : null;
          if (!found || !found.applicable) {
            this.showDiscError(this.dataset.errInvalid);
            // Shopify keeps a non-applicable code on the cart; take it off again.
            return this.updateCodes(codes).then((res) => { if (res.ok) this.render(res.data.sections); });
          }
          input.value = '';
          this.render(data.sections);
          this.afterChange(data);
        })
        .catch(() => this.showDiscError(this.dataset.errInvalid))
        .finally(() => this.busy(false));
    }

    removeCode(code) {
      const codes = this.currentCodes().filter((c) => c.toLowerCase() !== String(code).toLowerCase());
      this.showDiscError('');
      this.busy(true);
      return this.updateCodes(codes)
        .then(({ ok, data }) => {
          if (!ok) return this.refresh();
          this.render(data.sections);
          this.afterChange(data);
        })
        .catch(() => this.refresh())
        .finally(() => this.busy(false));
    }

    /* ---------- checkout ---------- */

    watchCheckout() {
      const mount = this.querySelector('[data-acd-sr]');
      const fallback = this.querySelector('[data-acd-fallback]');
      if (!mount || !fallback) return;
      const hasSr = () => !!mount.querySelector('.sr-headless-checkout');
      const sync = () => { if (hasSr()) fallback.hidden = true; };
      this.srObserver = new MutationObserver(sync);
      this.srObserver.observe(mount, { childList: true, subtree: true });
      const delay = (parseInt(this.dataset.fallbackDelay, 10) || 5) * 1000;
      this.fallbackTimer = setTimeout(() => { if (!hasSr()) fallback.hidden = false; }, delay);
    }

    checkoutFallback(e) {
      if (window.shiprocketCheckoutEvents && typeof window.shiprocketCheckoutEvents.buyCart === 'function') {
        window.shiprocketCheckoutEvents.buyCart(e);
      } else if (window.HeadlessCheckout && typeof window.HeadlessCheckout.checkout === 'function') {
        window.HeadlessCheckout.checkout(e);
      } else {
        window.location.href = root() + 'checkout';
      }
    }
  }

  customElements.define('akoviya-cart-drawer', AkoviyaCartDrawer);

  // Dawn's product form calls cart-notification's renderContents after an add; open the drawer instead.
  customElements.whenDefined('cart-notification').then(() => {
    const CartNotification = customElements.get('cart-notification');
    if (!CartNotification || CartNotification.prototype.acdWrapped) return;
    const original = CartNotification.prototype.renderContents;
    CartNotification.prototype.renderContents = function (parsedState) {
      const drawer = document.querySelector('akoviya-cart-drawer');
      if (!drawer || drawer.dataset.openAdd !== 'true') return original.call(this, parsedState);
      try {
        const bubble = parsedState && parsedState.sections && parsedState.sections['cart-icon-bubble'];
        const target = document.getElementById('cart-icon-bubble');
        if (bubble && target) target.innerHTML = this.getSectionInnerHTML(bubble);
      } catch (err) { /* the drawer refresh below updates the bubble too */ }
      if (this.header && this.header.reveal) this.header.reveal();
      drawer.refresh().finally(() => drawer.open(this.activeElement));
    };
    CartNotification.prototype.acdWrapped = true;
  });
})();
